import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, fieldErrors, unwrap } from '../../../api/client';
import type { AssetDetail, TaxRecord } from '../../../api/types';
import { Button, Card, CardHeader, ConfirmDialog, DateInput, Dialog, EmptyState, ErrorBox, Field, FormGrid, Input, Loading, nullIfEmpty, Table, Td, Textarea, Th, Tr, useToast } from '../../../components/ui';
import { centsToInput, formatDate, formatMoney, parseMoney } from '../../../lib/format';

type Form = {
  fiscal_year_label: string;
  fiscal_year_end: string;
  tax_asset_number: string;
  ahk: string;
  ahk_date: string;
  useful_life_months: string;
  depreciation_rate: string;
  annual_depreciation: string;
  ahk_fy_end: string;
  book_value: string;
  notes: string;
};

const toForm = (r: TaxRecord | null, asset: AssetDetail): Form => ({
  fiscal_year_label: r?.fiscal_year_label ?? String(new Date().getFullYear()),
  fiscal_year_end: r?.fiscal_year_end ?? `${new Date().getFullYear()}-12-31`,
  tax_asset_number: r?.tax_asset_number ?? asset.accounting_asset_number ?? '',
  ahk: centsToInput(r ? r.ahk_cents : asset.cost_net_cents),
  ahk_date: r?.ahk_date ?? asset.capitalization_date ?? asset.purchase_date ?? '',
  useful_life_months: r?.useful_life_months?.toString() ?? '',
  depreciation_rate: r?.depreciation_rate?.toString().replace('.', ',') ?? '',
  annual_depreciation: centsToInput(r?.annual_depreciation_cents),
  ahk_fy_end: centsToInput(r?.ahk_fy_end_cents),
  book_value: centsToInput(r?.book_value_cents),
  notes: r?.notes ?? '',
});

export function TaxTab({ asset, canWrite, canDelete }: { asset: AssetDetail; canWrite: boolean; canDelete: boolean }) {
  const [editing, setEditing] = useState<TaxRecord | 'new' | null>(null);
  const [deleting, setDeleting] = useState<TaxRecord | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const del = useMutation({
    mutationFn: (r: TaxRecord) =>
      unwrap(api.DELETE('/api/assets/{ref}/tax-records/{recordId}', { params: { path: { ref: asset.id, recordId: r.id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset', asset.id] });
      toast('Steuerwerte gelöscht');
      setDeleting(null);
    },
  });
  const q = useQuery({
    queryKey: ['asset', asset.id, 'tax'],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}/tax-records', { params: { path: { ref: asset.id } } })),
  });

  return (
    <Card>
      <CardHeader
        title="Steuerliche Werte je Wirtschaftsjahr"
        description="AHK, Nutzungsdauer, AfA, Buchwert – z. B. aus der Anlagenbuchhaltung übernommen."
        actions={
          canWrite && (
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
              Wirtschaftsjahr erfassen
            </Button>
          )
        }
      />
      {q.isPending ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} className="m-4" />
      ) : q.data.data.length === 0 ? (
        <EmptyState title="Keine Steuerwerte erfasst" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>WJ</Th>
              <Th>Anlagen-Nr.</Th>
              <Th className="text-right">AHK</Th>
              <Th>AHK-Datum</Th>
              <Th className="text-right">ND</Th>
              <Th className="text-right">AfA %</Th>
              <Th className="text-right">AfA/Jahr</Th>
              <Th className="text-right">Buchwert</Th>
              {(canWrite || canDelete) && <Th />}
            </tr>
          </thead>
          <tbody>
            {q.data.data.map((r) => (
              <Tr key={r.id}>
                <Td className="font-medium">
                  {r.fiscal_year_label}
                  <div className="text-xs font-normal text-slate-500">bis {formatDate(r.fiscal_year_end)}</div>
                </Td>
                <Td>{r.tax_asset_number ?? '–'}</Td>
                <Td className="text-right whitespace-nowrap">{formatMoney(r.ahk_cents, r.currency)}</Td>
                <Td>{formatDate(r.ahk_date)}</Td>
                <Td className="text-right">{r.useful_life_months ? `${r.useful_life_months} Mon.` : '–'}</Td>
                <Td className="text-right">{r.depreciation_rate !== null ? r.depreciation_rate.toLocaleString('de-DE') : '–'}</Td>
                <Td className="text-right whitespace-nowrap">{formatMoney(r.annual_depreciation_cents, r.currency)}</Td>
                <Td className="text-right font-medium whitespace-nowrap">{formatMoney(r.book_value_cents, r.currency)}</Td>
                {(canWrite || canDelete) && (
                  <Td className="text-right whitespace-nowrap">
                    {canWrite && (
                      <Button size="sm" variant="ghost" aria-label="Bearbeiten" icon={<Pencil className="size-4" />} onClick={() => setEditing(r)} />
                    )}
                    {canDelete && (
                      <Button size="sm" variant="ghost" aria-label="Löschen" icon={<Trash2 className="size-4 text-red-600" />} onClick={() => setDeleting(r)} />
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      <TaxDialog asset={asset} record={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => {
          setDeleting(null);
          del.reset();
        }}
        title={`Steuerwerte ${deleting?.fiscal_year_label ?? ''} löschen?`}
        message={
          <>
            Der Eintrag{deleting?.tax_asset_number ? ` mit Anlagennummer ${deleting.tax_asset_number}` : ''} wird entfernt. Beim nächsten Inventarabgleich mit
            „Werte ins Tool übernehmen“ wird er neu angelegt. In der Historie bleibt er vollständig erhalten.
          </>
        }
        confirmLabel="Löschen"
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </Card>
  );
}

function TaxDialog({ asset, record, onClose }: { asset: AssetDetail; record: TaxRecord | 'new' | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const existing = record && record !== 'new' ? record : null;
  const [f, setF] = useState<Form>(() => toForm(null, asset));
  useEffect(() => {
    if (record) setF(toForm(existing, asset));
  }, [record]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = useMutation({
    mutationFn: () => {
      const money = (s: string) => {
        const v = parseMoney(s);
        if (Number.isNaN(v)) throw new Error('Bitte Beträge im Format 1.234,56 eingeben.');
        return v;
      };
      const rate = f.depreciation_rate.trim() ? Number(f.depreciation_rate.replace(',', '.')) : null;
      const values = {
        tax_asset_number: nullIfEmpty(f.tax_asset_number),
        ahk_cents: money(f.ahk),
        ahk_date: nullIfEmpty(f.ahk_date),
        useful_life_months: f.useful_life_months.trim() ? Number(f.useful_life_months) : null,
        depreciation_rate: rate,
        annual_depreciation_cents: money(f.annual_depreciation),
        ahk_fy_end_cents: money(f.ahk_fy_end),
        book_value_cents: money(f.book_value),
        notes: nullIfEmpty(f.notes),
      };
      if (existing) {
        return unwrap(
          api.PATCH('/api/assets/{ref}/tax-records/{recordId}', {
            params: { path: { ref: asset.id, recordId: existing.id } },
            body: { ...values, fiscal_year_label: f.fiscal_year_label.trim() },
          }),
        );
      }
      return unwrap(
        api.POST('/api/assets/{ref}/tax-records', {
          params: { path: { ref: asset.id } },
          body: { ...values, fiscal_year_label: f.fiscal_year_label.trim(), fiscal_year_end: f.fiscal_year_end, source: 'manual' },
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset', asset.id, 'tax'] });
      toast('Steuerwerte gespeichert');
      onClose();
    },
  });
  const errs = fieldErrors(m.error);
  const bind = (k: keyof Form) => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value })) });

  return (
    <Dialog
      open={!!record}
      onClose={onClose}
      size="lg"
      title={existing ? `Wirtschaftsjahr ${existing.fiscal_year_label}` : 'Wirtschaftsjahr erfassen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <FormGrid className="lg:grid-cols-3">
        <Field label="Wirtschaftsjahr" required error={errs.fiscal_year_label} hint="z. B. 2026 oder 2025/26">
          {(p) => <Input {...p} {...bind('fiscal_year_label')} />}
        </Field>
        <Field label="Ende des Wirtschaftsjahres" required error={errs.fiscal_year_end}>
          {(p) => <DateInput {...p} disabled={!!existing} {...bind('fiscal_year_end')} />}
        </Field>
        <Field label="Anlagennummer (Steuer)" error={errs.tax_asset_number}>
          {(p) => <Input {...p} {...bind('tax_asset_number')} />}
        </Field>
        <Field label="AHK (€)" error={errs.ahk_cents}>
          {(p) => <Input {...p} inputMode="decimal" {...bind('ahk')} />}
        </Field>
        <Field label="AHK-Datum" error={errs.ahk_date}>
          {(p) => <DateInput {...p} {...bind('ahk_date')} />}
        </Field>
        <Field label="Nutzungsdauer (Monate)" error={errs.useful_life_months}>
          {(p) => <Input {...p} type="number" min={0} {...bind('useful_life_months')} />}
        </Field>
        <Field label="AfA-Satz (%)" error={errs.depreciation_rate}>
          {(p) => <Input {...p} inputMode="decimal" {...bind('depreciation_rate')} />}
        </Field>
        <Field label="AfA pro Jahr (€)" error={errs.annual_depreciation_cents}>
          {(p) => <Input {...p} inputMode="decimal" {...bind('annual_depreciation')} />}
        </Field>
        <Field label="AHK Ende WJ (€)" error={errs.ahk_fy_end_cents}>
          {(p) => <Input {...p} inputMode="decimal" {...bind('ahk_fy_end')} />}
        </Field>
        <Field label="Buchwert Ende WJ (€)" error={errs.book_value_cents}>
          {(p) => <Input {...p} inputMode="decimal" {...bind('book_value')} />}
        </Field>
        <Field label="Notizen" className="sm:col-span-2 lg:col-span-3">
          {(p) => <Textarea {...p} rows={2} {...bind('notes')} />}
        </Field>
      </FormGrid>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
